import React from 'react';
import { motion } from 'framer-motion';

const shifts: Array<{
  number: string;
  title: string;
  bullets: React.ReactNode[];
  source: string;
  accent: string;
}> = [
  {
    number: '01',
    title: 'Pedagogical AI Shift',
    bullets: [
      <>Chatbots promote cognitive laziness.</>,
      <>
        Market demands native{' '}
        <span className="text-white font-semibold">multi-year mastery tracking</span>.
      </>,
    ],
    source: 'OECD Digital Education Outlook 2026',
    accent: '#a855f7',
  },
  {
    number: '02',
    title: 'Administrative Relief',
    bullets: [
      <>
        <span className="text-white font-semibold">60%</span> of teachers report
        unsustainable burnout.
      </>,
      <>
        Boards prioritise automated workflows for{' '}
        <span className="text-white font-semibold">institutional retention</span>.
      </>,
    ],
    source: 'Australia EdTech Market Report 2026 — Trends, Growth & Forecasts',
    accent: '#f59e0b',
  },
  {
    number: '03',
    title: 'B2B2C Confluence',
    bullets: [
      <>
        Intercepting the{' '}
        <span className="text-white font-semibold">$11B+ shadow market</span>.
      </>,
      <>Conversions driven via native school data, not ad spend.</>,
    ],
    source:
      'Australian Tutoring Association (ATA) Industry Audits; UNESCO IIEP Shadow Education Ecosystem Reports; University of Sydney/Wollongong Supplementary Academic Expenditure Data',
    accent: '#ec4899',
  },
  {
    number: '04',
    title: 'Data Sovereignty',
    bullets: [
      <>
        Compliance mandates{' '}
        <span className="text-white font-semibold">
          local residency & zero-retention AI
        </span>
        .
      </>,
      <>Global rivals can't clear the bar — Lumen8 builds it in by design.</>,
    ],
    source: "Gilbert + Tobin — Exposure Draft of Children's Online Privacy Code",
    accent: '#0ea5e9',
  },
];

const pillars: Array<{ label: string; body: React.ReactNode }> = [
  {
    label: 'Demand Coalescing',
    body: (
      <>
        Diagnostic AI and sovereign local data are{' '}
        <span className="text-white font-semibold">native to our core architecture</span> —
        not insecure add-ons.
      </>
    ),
  },
  {
    label: 'Incumbents Stalled',
    body: (
      <>
        Legacy platforms face{' '}
        <span className="text-white font-semibold">
          immense technical and legal friction
        </span>{' '}
        to re-engineer database structures for direct consumer monetisation.
      </>
    ),
  },
  {
    label: 'Pure Defensibility',
    body: (
      <>
        Launching as a compliant, multi-subject platform captures the{' '}
        <span className="text-white font-semibold">uncrowded intersection</span> before
        single-subject rivals can pivot.
      </>
    ),
  },
];

const FFSlide05WhyNow: React.FC = () => {
  return (
    <div className="flex flex-col items-center justify-center h-full px-12 py-8">
      <motion.h2
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-5xl font-bold text-white mb-3 text-center"
      >
        Why Now?
      </motion.h2>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-xl text-[#94a3b8] mb-8 text-center max-w-4xl"
      >
        Four structural shifts are moving the market{' '}
        <span className="text-white font-semibold">toward us</span>
      </motion.p>

      <div className="grid grid-cols-4 gap-4 w-full max-w-6xl mb-6">
        {shifts.map((s, i) => (
          <motion.div
            key={s.number}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 + i * 0.08 }}
            className="p-5 rounded-xl flex flex-col"
            style={{
              background: 'rgba(30, 41, 59, 0.6)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderTop: `3px solid ${s.accent}`,
            }}
          >
            <div
              className="text-sm font-semibold mb-2"
              style={{ color: s.accent }}
            >
              {s.number}
            </div>
            <h3 className="text-xl font-bold text-white mb-4 leading-tight">
              {s.title}
            </h3>
            <ul className="space-y-2.5 mb-5 flex-1">
              {s.bullets.map((b, idx) => (
                <li
                  key={idx}
                  className="flex gap-2.5 text-sm text-[#94a3b8] leading-snug"
                >
                  <span
                    aria-hidden
                    className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ background: s.accent }}
                  />
                  <span className="flex-1">{b}</span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-[#64748b] leading-snug">
              <span className="font-semibold">Source: </span>
              {s.source}
            </p>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="w-full max-w-6xl rounded-2xl overflow-hidden"
        style={{
          background:
            'linear-gradient(135deg, rgba(14, 165, 233, 0.22) 0%, rgba(6, 182, 212, 0.14) 50%, rgba(14, 165, 233, 0.1) 100%)',
          border: '1px solid rgba(14, 165, 233, 0.45)',
          boxShadow: '0 0 40px rgba(14, 165, 233, 0.12)',
        }}
      >
        <div className="h-[3px] w-full bg-gradient-to-r from-transparent via-[#0ea5e9] to-transparent" />

        <div className="px-8 py-5">
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="h-px w-10 bg-[#0ea5e9]" />
            <h3 className="text-base font-bold text-white uppercase tracking-[0.25em]">
              The Lumen8 Market Window
            </h3>
            <div className="h-px w-10 bg-[#0ea5e9]" />
          </div>

          <div className="grid grid-cols-3 gap-6">
            {pillars.map((p) => (
              <div key={p.label} className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <span
                    aria-hidden
                    className="w-1.5 h-1.5 rounded-full bg-[#0ea5e9]"
                  />
                  <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#0ea5e9]">
                    {p.label}
                  </div>
                </div>
                <p className="text-sm text-[#e2e8f0] leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default FFSlide05WhyNow;
